import { PF_PORTALS, RECON_LABELS, buildEmployeeRecon, buildPosition, compareBankInvoice } from "../../services/payrollFinanceCalculator";
import { RECON_TONE, dateLabel, money } from "./shared";

export default function OverviewTab({ data, position, asOf }) {
  const { totals, funds, rows } = position;
  const perPortal = PF_PORTALS.map((portal) => ({
    portal,
    ...buildPosition({ withdrawals: data.withdrawals, movements: data.movements, repayments: data.repayments, uses: data.uses, asOf, portal: portal.code }).totals
  }));
  const attention = rows.filter((row) => row.reconStatus !== "COMPLETE" && !row.repaid);
  const employees = buildEmployeeRecon(rows).filter((e) => e.withEmployee !== 0);
  const checked = rows.map((row) => compareBankInvoice({ ...row.withdrawal })).filter(Boolean);
  const bankDifferences = checked.filter((item) => !item.match);

  return (
    <>
      <section className="management-metrics">
        <article className="metric-card"><div className="metric-label">Withdrawn from bank</div><div className="metric-value">{money(totals.withdrawn)}</div></article>
        <article className="metric-card"><div className="metric-label">Principal outstanding</div><div className="metric-value">{money(totals.outstanding)}</div></article>
        <article className="metric-card"><div className="metric-label">Interest accrued to {dateLabel(asOf)}</div><div className="metric-value">{money(totals.accruedInterest)}</div></article>
        <article className="metric-card"><div className="metric-label">Payable if repaid on {dateLabel(asOf)}</div><div className="metric-value">{money(totals.payableNow)}</div></article>
      </section>
      <section className="management-metrics">
        <article className="metric-card"><div className="metric-label">Received into ALM accounts</div><div className="metric-value">{money(funds.received)}</div></article>
        <article className="metric-card"><div className="metric-label">Funds used by company</div><div className="metric-value">{money(funds.used)}</div></article>
        <article className="metric-card"><div className="metric-label">Funds on hand</div><div className="metric-value">{money(funds.onHand)}</div></article>
        <article className="metric-card"><div className="metric-label">Still with employees</div><div className="metric-value">{money(totals.withEmployee)}</div><div className="page-copy" style={{ fontSize: 12 }}>charges deducted {money(totals.charges)}</div></article>
      </section>

      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">By portal — as of {dateLabel(asOf)}</h2></div>
        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>Portal</th><th>Withdrawn</th><th>Repaid (principal)</th><th>Outstanding</th><th>Interest paid</th><th>Interest accrued</th><th>Payable now</th><th>Still with employees</th></tr></thead>
            <tbody>
              {perPortal.map(({ portal, ...t }) => (
                <tr key={portal.code}><td>{portal.label}</td><td>{money(t.withdrawn)}</td><td>{money(t.repaidPrincipal)}</td><td>{money(t.outstanding)}</td><td>{money(t.interestPaid)}</td><td>{money(t.accruedInterest)}</td><td><strong>{money(t.payableNow)}</strong></td><td>{money(t.withEmployee)}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="page-copy">Interest is 1.9% a month (22.8% a year) charged per day on principal still outstanding, counted from the withdrawal date. Change the "as of" date above to see what repaying on any day would cost.</p>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">Reconciliation — needs attention</h2></div>
        <div className="panel-body table-wrap">
          {bankDifferences.length > 0 && <div className="management-alert error">{bankDifferences.length} withdrawal(s) have interest on the bank's invoice that differs from our calculation — see the Withdrawals tab.</div>}
          <table className="data-table">
            <thead><tr><th>Withdrawn</th><th>Employee</th><th>Amount</th><th>Returned to ALM</th><th>Charges</th><th>Still with employee</th><th>Status</th></tr></thead>
            <tbody>
              {attention.map((row) => (
                <tr key={row.withdrawal.id}>
                  <td>{dateLabel(row.withdrawal.date, row.withdrawal.time)}</td><td>{row.withdrawal.employeeName}</td><td>{money(row.principal)}</td>
                  <td>{money(row.returned)}</td><td>{money(row.charges)}</td><td>{money(row.withEmployee)}</td>
                  <td><span className={`status-pill ${RECON_TONE[row.reconStatus]}`}>{RECON_LABELS[row.reconStatus]}</span></td>
                </tr>
              ))}
              {!attention.length && <tr><td colSpan={7}>Every open withdrawal is fully reconciled.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header"><h2 className="panel-title">Employee accounts holding money</h2></div>
        <div className="panel-body table-wrap">
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Bank / account</th><th>Received</th><th>Returned to ALM</th><th>Charges</th><th>Still held</th></tr></thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.employeeId || e.name}><td>{e.name}</td><td>{[e.bank, e.account].filter(Boolean).join(" · ") || "—"}</td><td>{money(e.withdrawn)}</td><td>{money(e.returned)}</td><td>{money(e.charges)}</td><td><strong>{money(e.withEmployee)}</strong></td></tr>
              ))}
              {!employees.length && <tr><td colSpan={6}>No employee account is holding money.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
